import { toast } from "react-toastify";
import { endpoints } from "../../../configs/Settings";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAxios } from "../../../utils/useAxios";
import { getAuthToken } from "@/services/auth/OAuthApplication";

interface IProps {
  id?: string;
  /** IDs de schedules separados por vírgula para busca múltipla via _id */
  _id?: string;
  actor?: string;
  location?: string;
  includeSlot?: boolean;
  count?: number;
  active?: boolean;
  startDate?: string;
  specialty?: string;
  city?: string;
  state?: string;
  /**
   * Organização(s) para filtrar schedules.
   * - Aceita string única (ex.: "Organization/2.16...-cnpj")
   * - Aceita múltiplas (OR) separadas por vírgula: "org1,org2,org3"
   * - Aceita array e serializa como CSV.
   */
  organization?: string | string[];
  practitioner?: string;
  includeLocation?: boolean;
  /** Inclui HealthcareService referenciado em Schedule.actor */
  includeHealthcareService?: boolean;
  sort?: string;
  /**
   * Filtro FHIR service-category (CodeableConcept).
   * Aceita code, system|code ou token conforme o servidor.
   * Opcional — callers existentes não são afetados.
   */
  serviceCategory?: string;
}

/**
 * @param id schedule id
 * @param _id IDs de schedules separados por vírgula para busca múltipla (ex: "id1,id2,id3")
 * @param actor id
 * @param location id
 * @param includeSlot boolean, it doesn't filter slots by any property
 * @param count int
 * @param active boolean
 * @param startdate YYYY-MM-DD
 * @param specialty from EspecialidadeMedica value set
 * @param city name, e.g. Brasília
 * @param state acronym, e.g. DF
 * @param organization {oid.cnpj}-{cnpj} ou Organization/{oid.cnpj}-{cnpj}. Para múltiplas, usar CSV (OR).
 * @param practitioner {oid.cpf}-{cpf} or Practitioner/{oid.cpf}-{cpf}
 * @param includeLocation boolean
 * @param sort string - critérios adicionais de ordenação além de -_lastUpdated
 * @param serviceCategory token FHIR service-category (code ou system|code)
 * @returns Bundle
 */
export async function GetSchedule({
  id,
  _id,
  actor,
  location,
  includeSlot,
  count,
  active,
  startDate,
  specialty,
  city,
  state,
  organization,
  practitioner,
  includeLocation,
  includeHealthcareService,
  sort,
  serviceCategory,
}: IProps) {
  // Construir URL base
  let baseUrl = `${endpoints.WORKFLOWFHIR}/Schedule`;

  // Se tiver ID único, retorna direto o recurso
  if (id) {
    baseUrl = `${baseUrl}/${id}`;
  }

  // Construir parâmetros de query
  const queryParams: string[] = [];

  // Adicionar parâmetros de paginação
  queryParams.push(`_count=${count || 250}`);

  // Se tiver _id (múltiplos IDs), adiciona como parâmetro de busca
  // Formato: _id=id1,id2,id3
  if (_id && !id) {
    queryParams.push(`_id=${encodeURIComponent(_id)}`);
  }

  // Adicionar parâmetros de busca
  if (actor) {
    queryParams.push(`actor=${encodeURIComponent(actor)}`);
  }

  if (location) {
    queryParams.push(`actor=${encodeURIComponent(location)}`);
  }

  if (includeSlot) {
    queryParams.push("_revinclude=Slot:schedule");
  }

  if (active) {
    queryParams.push("active=true");
  }

  if (startDate) {
    queryParams.push(
      `_has:Slot:schedule:start=${encodeURIComponent(startDate)}`
    );
  }

  if (specialty) {
    queryParams.push(`actor.name:exact=${encodeURIComponent(specialty)}`);
  }

  if (city) {
    queryParams.push(`actor.address-city=${encodeURIComponent(city)}`);
  }

  if (state) {
    queryParams.push(`actor.address-state=${encodeURIComponent(state)}`);
  }

  if (organization) {
    const orgCsv = Array.isArray(organization)
      ? organization.join(",")
      : organization;
    queryParams.push(`actor.organization=${encodeURIComponent(orgCsv)}`);
  }

  if (practitioner) {
    queryParams.push(`actor=${encodeURIComponent(practitioner)}`);
  }

  if (includeLocation) {
    queryParams.push("_include=Schedule:actor:Location");
  }

  if (includeHealthcareService) {
    queryParams.push("_include=Schedule:actor:HealthcareService");
  }

  // Filtro opcional por serviceCategory (ex.: Pronto Atendimento)
  if (serviceCategory) {
    queryParams.push(
      `service-category=${encodeURIComponent(serviceCategory)}`,
    );
  }

  // Adicionar critérios de ordenação
  const sortCriteria = sort
    ? `-_lastUpdated,${encodeURIComponent(sort)}`
    : "-_lastUpdated";
  queryParams.push(`_sort=${sortCriteria}`);

  // Construir URL final
  const url =
    !id && queryParams.length > 0
      ? `${baseUrl}?${queryParams.join("&")}`
      : baseUrl;

  const headers = {
    Accept: "*/*",
    Authorization: await getAuthToken(),
    "Content-Type": "application/fhir+json",
  };
  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Get Schedule: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetSchedule ", error);
      // toast.error(`GetSchedule Error: ${error?.response?.status}`);
      throw new Error();
    });
  if (response) return response;
}
